import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Avatar } from '../ui/Avatar';
import { Spinner } from '../ui/Spinner';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useAssignTicket } from '../../hooks/queries/useTickets';
import { ROLE_LABELS } from '../../services/auth/permissions';
import type { CollaboratorRole } from '../../services/auth/permissions';
import type { TicketWithRelations } from '../../services/tickets/ticketService';

interface TicketAssignModalProps {
  ticket: TicketWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketAssignModal({ ticket, open, onOpenChange }: TicketAssignModalProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(ticket.assigned_to);
  const [message, setMessage] = useState('');

  const { data: collaborators, isLoading } = useCollaborators();
  const assignTicket = useAssignTicket();

  const activeCollaborators = (collaborators ?? []).filter((c) => c.is_active);

  function isCompetent(collaboratorCategories: Array<{ id: string }>): boolean {
    if (!ticket.category_id) return false;
    return collaboratorCategories.some((cat) => cat.id === ticket.category_id);
  }

  const filtered = activeCollaborators
    .filter((c) => c.full_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aComp = isCompetent(a.categories) ? 0 : 1;
      const bComp = isCompetent(b.categories) ? 0 : 1;
      if (aComp !== bComp) return aComp - bComp;
      return a.full_name.localeCompare(b.full_name, 'fr');
    });

  function handleSubmit() {
    if (!selectedId) return;
    assignTicket.mutate(
      { ticketId: ticket.id, collaboratorId: selectedId, message: message.trim() || undefined },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40 animate-in fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-cime-surface rounded-cime shadow-xl border border-neutral-200 outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-cime-text-primary">
              Désigner l'avocat en charge
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Ticket context */}
          <div className="px-5 pt-3 pb-2">
            <p className="text-sm text-neutral-500">
              Ticket de{' '}
              <span className="font-medium text-neutral-700">
                {ticket.sender_name ?? 'Expéditeur inconnu'}
              </span>
            </p>
          </div>

          {/* Search */}
          <div className="px-5 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Rechercher un collaborateur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Collaborator list */}
          <div className="px-3 pb-3 max-h-72 overflow-y-auto space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-neutral-400 text-center py-4">
                Aucun collaborateur trouvé
              </p>
            ) : (
              filtered.map((collab) => {
                const competent = isCompetent(collab.categories);
                const isSelected = selectedId === collab.id;

                return (
                  <button
                    key={collab.id}
                    type="button"
                    onClick={() => setSelectedId(isSelected ? null : collab.id)}
                    className={[
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                      isSelected
                        ? 'bg-cime-primary/10 border border-cime-primary'
                        : 'hover:bg-neutral-50 border border-transparent',
                    ].join(' ')}
                  >
                    <Avatar
                      name={collab.full_name}
                      src={collab.avatar_url ?? undefined}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">
                        {collab.full_name}
                      </p>
                      <p className="text-xs text-neutral-400 truncate">
                        {ROLE_LABELS[collab.role as CollaboratorRole] ?? collab.role}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {competent && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                          Compétent
                        </span>
                      )}
                      {collab.categories.slice(0, 2).map((cat) => (
                        <span
                          key={cat.id}
                          className="text-xs bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded-full hidden sm:inline"
                        >
                          {cat.label}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Optional message to assignee */}
          {selectedId && (
            <div className="px-5 pb-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message à l'assigné (optionnel — inclus dans l'email de notification)..."
                rows={2}
                className="w-full text-sm border border-neutral-300 rounded-cime px-3 py-2 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent resize-none"
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-neutral-200">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm">
                Annuler
              </Button>
            </Dialog.Close>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!selectedId || assignTicket.isPending}
            >
              {assignTicket.isPending ? 'Assignation...' : 'Assigner'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
